import { CustomDatePipe, FullNamePipe, MatButtonLoaderStandaloneDirective } from '@abovo/shared-angular-utils';
import { ACL_TARGET_APPROVALS, AclRestrictDirective, getCurrentUser, useEntityPermission } from '@abovo/shared-auth';
import {
   AbovoIconProp,
   ApprovalRequest,
   Client,
   CreateApprovalReviewer,
   CustomCardHeaderButtonAction,
   EntityPermissionRestriction,
   PermissionKeys,
   Restriction,
   UserEmployeeDataTransformed,
} from '@abovo/shared-models';
import {
   CardActionsComponent,
   CardComponent,
   CardContentComponent,
   CardHeaderComponent,
   DropdownInputComponent,
   InitialsAvatarComponent,
   SkeletonLoaderModule,
   SkeletonSubConfig,
   SkeletonTypes,
} from '@abovo/shared-ui-components';
import { ApprovalRequestStore, getCurrentMediaPlan } from '@abovo/webvis-data-access';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, signal, Signal, untracked } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { isSameDay } from 'date-fns';
import { SHARE_LINK_TARGET_ATTACHMENT, ShareLinkIndicatorComponent } from '../../../../../../features/share-link';
import {
   ApprovalRequestDialogInput,
   ApprovalStatusBadgeComponent,
   constructApprovalReviewer,
   getDebriefDeadline,
   getFeedbackFirstPlanDeadline,
   getFirstMediaplanSharingDeadline,
   RequestStatusSlugs,
   ReviewerRoleSlugs,
   ReviewerStatusSlugs,
   useApprovalFeedback,
   useApprovalRequest,
} from '../../../../../approval';

@Component({
   selector: 'webvis-feature-mediaplan-approval',
   imports: [
      CommonModule,
      CardActionsComponent,
      CardComponent,
      CardContentComponent,
      MatButtonModule,
      TranslatePipe,
      CardHeaderComponent,
      CustomDatePipe,
      DropdownInputComponent,
      SkeletonLoaderModule,
      ReactiveFormsModule,
      FontAwesomeModule,
      MatButtonLoaderStandaloneDirective,
      FullNamePipe,
      InitialsAvatarComponent,
      AclRestrictDirective,
      ApprovalStatusBadgeComponent,
      MatTooltip,
      ShareLinkIndicatorComponent,
   ],
   templateUrl: './mediaplan-approval.component.html',
   changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MediaplanApprovalComponent {
   private store = inject(Store);
   approvalRequestStore = inject(ApprovalRequestStore);
   private translateService = inject(TranslateService);

   private useApprovalRequest = useApprovalRequest();
   private useApprovalFeedback = useApprovalFeedback();

   private currentUser = this.store.selectSignal(getCurrentUser);
   mediaplan = this.store.selectSignal(getCurrentMediaPlan);
   approvalRequestControl = new FormControl<ApprovalRequest | null>(null);
   currentApproval = signal<ApprovalRequest | null>(null);
   private useEntityPermission = useEntityPermission();
   private allowEdit = this.useEntityPermission(this.entityRestriction('can_edit', 'MediaplanApprovalComponent edit approval card action'));
   cancelPermission = this.entityRestriction('can_cancel', 'MediaplanApprovalComponent cancel approval button');
   submitPermission = this.entityRestriction('can_submit', 'MediaplanApprovalComponent notify client button');
   editPermission = this.entityRestriction('can_edit', 'MediaplanApprovalComponent reinstate approval button');
   reviewPermission = this.entityRestriction('can_review', 'MediaplanApprovalComponent approve and reject buttons');
   approvedBy = computed(() => this.currentApproval()?.reviewers?.filter((reviewer) => reviewer.status?.slug === 'approved') ?? []);
   rejectedBy = computed(() => this.currentApproval()?.reviewers?.filter((reviewer) => reviewer.status?.slug === 'rejected') ?? []);
   approvalPending = computed(() => {
      const others = [...this.approvedBy().map((a) => a.id), ...this.rejectedBy().map((r) => r.id)];
      return this.currentApproval()?.reviewers?.filter((reviewer) => !others.includes(reviewer.id)) ?? [];
   });
   hasDriveLink = computed(() => !!this.currentApproval()?.metadata);

   /** Attachment id of the deck PDF; null for drive-link-only approvals (nothing to share). */
   attachmentId = computed(() => this.currentApproval()?.approval_request_attachments?.[0]?.attachment_id ?? null);
   readonly shareLinkTargetConfig = SHARE_LINK_TARGET_ATTACHMENT;
   private editDisabled$ = toObservable(computed(() => this.approvalRequestStore.loading() || this.createButtonSettings().disabled));
   approvalsDropdownData = computed(() =>
      // Add a version text for the dropdown template, approvals are received newest first from the backend by default
      this.approvalRequestStore.entities().map((approval, index, array) => ({ ...approval, displayName: `v${array.length - index} - ${approval.title}` })),
   );
   approvalCreateRule: Restriction = {
      target: ACL_TARGET_APPROVALS,
      operation: 'create',
      description: 'MediaplanApprovalComponent create approval request dialog button',
   };
   deadlineTranslationString = computed(() => {
      const statusSlug = this.currentApproval()?.status?.slug;
      switch (statusSlug) {
         case RequestStatusSlugs.rejected:
         case RequestStatusSlugs.draft:
         case RequestStatusSlugs.pending:
            return `mediaplan_approval.deadline_${statusSlug}`;
         default:
            return 'mediaplan_approval.deadline';
      }
   });

   createButtonSettings = computed(() => {
      const briefingStatusSlugs = ['client_briefing', 'client_briefing_reopened', 'client_debriefing', 'client_debriefing_rejected'];
      const mediaplanStatus = this.mediaplan()?.latest_media_plan_status?.slug;
      return {
         disabled: !!mediaplanStatus && briefingStatusSlugs.includes(mediaplanStatus),
         tooltip: this.getCreateButtonTooltip(mediaplanStatus),
      };
   });
   descriptionsCardActions: Signal<Array<CustomCardHeaderButtonAction>> = computed(() => {
      const approval = this.currentApproval();
      const allowEdit = this.allowEdit();
      return untracked(() => {
         const view =
            approval && !allowEdit
               ? [
                    {
                       fn: () => this.openApprovalRequest(),
                       icon: ['far', 'eye'] as AbovoIconProp,
                       tooltipMsg: this.translateService.instant('general.view'),
                       disabled: () => this.editDisabled$,
                    },
                 ]
               : [];
         const edit =
            approval && allowEdit
               ? [
                    {
                       fn: () => this.openApprovalDialog(approval),
                       icon: ['far', 'pencil-alt'] as AbovoIconProp,
                       tooltipMsg: this.translateService.instant('general.edit'),
                       disabled: () => this.editDisabled$,
                    },
                 ]
               : [];
         const create = [
            {
               fn: () => this.openApprovalDialog(),
               icon: ['far', 'plus'] as AbovoIconProp,
               tooltipMsg: this.translateService.instant('general.add'),
               disabled: () => this.editDisabled$,
               aclRule: this.approvalCreateRule,
            },
         ];
         const del = approval
            ? [
                 {
                    fn: () => this.deleteApproval(approval),
                    icon: ['far', 'trash-alt'] as AbovoIconProp,
                    tooltipMsg: this.translateService.instant('general.delete'),
                    disabled: () => this.editDisabled$,
                    aclRule: {
                       target: ACL_TARGET_APPROVALS,
                       operation: 'delete',
                       description: 'MediaplanApprovalComponent delete approval request dialog button',
                    },
                 },
              ]
            : [];
         return [...del, ...view, ...edit, ...create];
      });
   });
   skeletonLoadConfig: SkeletonSubConfig = {
      styles: {
         height: '100px',
      },
      appearance: {
         columns: 1,
         avatar: false,
         rows: 2,
         divider: false,
      },
   };
   skeletonType = SkeletonTypes.tableRow;

   /**
    * Load approvals was moved to {@link MediaPlanGeneralComponent} to allow checking if this component needs to be rendered
    */

   private approvalRequestsEffect = effect(() => {
      const approvalRequests = this.approvalRequestStore.entities();
      untracked(() => {
         const currentApproval = this.currentApproval();
         if (approvalRequests?.length) {
            // Requests are retrieved in id descending order, meaning the forst one is the most recent
            // In case of an update we reselect the current request, if that request was not cancelled
            // If the list contains only one element, we always select that one
            const currentlySelected =
               approvalRequests.length > 1
                  ? // More than one element in the list, look for our current request if that's not cancelled and take the first one that's not cancelled if it cannot be found
                    approvalRequests.find(
                       (request) => request.status?.slug !== RequestStatusSlugs.cancelled && (currentApproval ? request.id === currentApproval.id : true),
                    ) || approvalRequests.find((request) => request.status?.slug !== RequestStatusSlugs.cancelled)
                  : // list contains one item, select it
                    approvalRequests[0];
            this.approvalRequestControl.setValue(currentlySelected ?? null);
            this.currentApproval.set(currentlySelected ?? null);
         } else {
            this.currentApproval.set(null);
         }
      });
   });

   cancelApproval() {
      const currentApproval = this.currentApproval();
      if (!currentApproval) {
         return;
      }
      this.approvalRequestStore.update({ id: currentApproval.id, status: { slug: RequestStatusSlugs.cancelled } });
   }

   sendNotification() {
      const currentApproval = this.currentApproval();
      if (!currentApproval) {
         return;
      }
      // new deadline for proposal feedback
      const calculatedShareDeadlne = getFirstMediaplanSharingDeadline(this.mediaplan()?.start_date ?? new Date());
      // If somebody entered a different deadline for this request then we keep that date, otherwise update with the default period
      const newDeadline =
         currentApproval.deadline && isSameDay(calculatedShareDeadlne, currentApproval.deadline)
            ? getFeedbackFirstPlanDeadline(this.mediaplan()?.start_date ?? new Date())
            : currentApproval.deadline;
      this.approvalRequestStore.update({ id: currentApproval.id, status: { slug: RequestStatusSlugs.pending }, deadline: newDeadline });
   }

   reinstate() {
      const currentApproval = this.currentApproval();
      if (!currentApproval) {
         return;
      }
      this.approvalRequestStore.update({ id: currentApproval.id, status: { slug: RequestStatusSlugs.draft } });
   }

   private deleteApproval(approval: ApprovalRequest) {
      this.approvalRequestStore.delete(approval.id);
   }

   onVersionChange(approval?: ApprovalRequest) {
      this.currentApproval.set(approval ?? null);
   }

   openApprovalRequest(approve?: boolean) {
      const approval = this.currentApproval();
      if (!approval) return;
      this.useApprovalFeedback(approval, { approve });
   }

   approveReject(approve: boolean) {
      const currentApproval = this.currentApproval();
      const currentUser = this.currentUser();
      if (!currentApproval || !currentUser) {
         return;
      }
      const currentReviewer = this.getCurrentReviewer(approve, currentApproval, currentUser);
      const otherReviewers = (currentApproval.reviewers ?? []).filter((reviewer) => reviewer.user?.id !== currentUser.id);
      const reviewers = [...otherReviewers, currentReviewer];
      const approval = {
         ...currentApproval,
         reviewers,
      };
      this.openApprovalDialog(approval);
   }

   private getCurrentReviewer(approve: boolean, currentApproval: ApprovalRequest, currentUser: UserEmployeeDataTransformed) {
      const currentReviewer = currentApproval.reviewers?.find((reviewer) => reviewer.user?.id === currentUser.id);
      if (currentReviewer) {
         return { ...currentReviewer, status: { slug: approve ? ReviewerStatusSlugs.approved : ReviewerStatusSlugs.rejected } };
      }
      const newReviewer: CreateApprovalReviewer = {
         user: {
            id: currentUser.id,
            first_name: currentUser.first_name,
            last_name: currentUser.last_name,
         },
         role_type: { slug: ReviewerRoleSlugs.optional },
         status: { slug: approve ? ReviewerStatusSlugs.approved : RequestStatusSlugs.rejected },
      };
      return newReviewer;
   }

   openApprovalDialog(approval?: ApprovalRequestDialogInput) {
      // Due date for new approval requests is 11 weeks before start of media plan, fall back to today as start date just in case the plan is broken
      // That is, unless the most recent approval request exists and has been rejected
      const mediaplan = this.mediaplan();
      if (!mediaplan?.id) {
         return;
      }
      const previousRequest = this.approvalRequestStore.entities().at(0);
      const previousRequestRejected = previousRequest?.status?.slug === RequestStatusSlugs.rejected;
      const deadline = previousRequestRejected ? getDebriefDeadline(new Date()) : getFirstMediaplanSharingDeadline(mediaplan.start_date ?? new Date());
      const approvalRequest: ApprovalRequestDialogInput = approval ?? {
         items: [{ entity_id: mediaplan.id, entity_type: 'campaign' }],
         client: mediaplan.client as Client & { id: number },
         title: mediaplan.name ?? '',
         deadline,
         ...(previousRequestRejected && {
            title: previousRequest.title,
            reviewers: previousRequest?.reviewers?.map<CreateApprovalReviewer>((reviewer) => ({
               user: reviewer.user,
               role_type: { slug: reviewer.role_type.slug },
               status: { slug: ReviewerStatusSlugs.pending },
            })),
         }),
      };
      const preSelectedReviewers = (mediaplan.mediaplan_contacts ?? [])
         .filter((contact) => contact.effective_role === 'client_admin')
         .map((contact) => constructApprovalReviewer(contact.contact, ReviewerRoleSlugs.required));

      this.useApprovalRequest({
         approvalRequest,
         preSelectedReviewers,
      });
   }

   /** Keeps the restriction object stable, so the acl directive does not rebuild the view on every cycle. */
   private entityRestriction(permission: PermissionKeys<ApprovalRequest>, description: string): Signal<EntityPermissionRestriction<ApprovalRequest>> {
      return computed(() => ({ entity: this.currentApproval(), permission, description }));
   }

   private getCreateButtonTooltip(mediaplanStatusSlug?: string) {
      switch (mediaplanStatusSlug) {
         case 'client_briefing':
            return 'mediaplan_approval.create_tooltip_briefing';
         case 'client_briefing_reopened':
         case 'client_debriefing':
         case 'client_debriefing_rejected':
            return 'mediaplan_approval.create_tooltip_debriefing';
         default:
            return null;
      }
   }
}
